import { isDefined } from "@clipboard-health/util-ts";
import { type CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { type CbhFeatureFlags } from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import {
  ShiftRateNegotiationRole,
  ShiftRateNegotiationStatus,
} from "@src/appV2/OpenShifts/RateNegotiation/types";
import { type ModalShiftData } from "@src/appV2/redesign/Shift/types";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format, isBefore, isSameDay, startOfDay, subDays } from "date-fns";
import { findLast } from "lodash";

import { formatDollarsAsUsd } from "../lib/Money/utils/currency";
import { type NegotiationShiftDetailsResponse } from "./api/useGetNegotiationShiftDetails";
import { NegotiationRateRole, type RateNegotiationHistoryResponse } from "./types";

const MIN_NEGOTIATION_PAY_RATE = 15;

function getLowerLimit(parameters: {
  flagLimits: CbhFeatureFlags[CbhFeatureFlag.RATE_NEGOTIATION_LIMITS];
  shiftPayRate: number;
  lastOfferFromHcf: number;
}) {
  const { flagLimits, lastOfferFromHcf, shiftPayRate } = parameters;

  if (lastOfferFromHcf > 0) {
    return lastOfferFromHcf;
  }

  if (shiftPayRate > 0) {
    return shiftPayRate;
  }

  if (flagLimits?.Minimum) {
    return Number(flagLimits?.Minimum);
  }

  return MIN_NEGOTIATION_PAY_RATE;
}

function getUpperLimit(parameters: {
  flagLimits: CbhFeatureFlags[CbhFeatureFlag.RATE_NEGOTIATION_LIMITS];
  agentReq: string | undefined;
  lastOfferByHcp: number;
  shiftPayRate: number;
}): number {
  const { flagLimits, agentReq, shiftPayRate, lastOfferByHcp } = parameters;

  if (lastOfferByHcp) {
    return lastOfferByHcp - 1;
  }

  if (agentReq && agentReq in flagLimits) {
    return Number(flagLimits[agentReq as keyof typeof flagLimits]);
  }

  if (flagLimits.Maximum) {
    return Number(flagLimits.Maximum);
  }

  if (shiftPayRate) {
    return 5 * shiftPayRate;
  }

  return Number.MAX_SAFE_INTEGER;
}

export function getLowerAndUpperLimit(parameters: {
  flagLimits: CbhFeatureFlags[CbhFeatureFlag.RATE_NEGOTIATION_LIMITS];
  shift: ModalShiftData | undefined;
  negotiationShiftDetails: NegotiationShiftDetailsResponse | undefined;
}) {
  const { flagLimits, shift, negotiationShiftDetails } = parameters;

  let lastOfferFromHcf = 0;
  let lastOfferByHcp = 0;

  const rateNegotiation = shift?.rateNegotiation;
  const negotiationHistory = rateNegotiation?.history ?? [];

  if (rateNegotiation?.lastOfferBy === ShiftRateNegotiationRole.WORKPLACE) {
    lastOfferFromHcf = rateNegotiation?.lastOffer?.hourlyPay ?? 0;
    lastOfferByHcp =
      findLast(negotiationHistory, (historyItem: RateNegotiationHistoryResponse) => {
        return historyItem.createdByUserRole === NegotiationRateRole.WORKER;
      })?.payRate ?? 0;
  } else if (rateNegotiation?.lastOfferBy === ShiftRateNegotiationRole.WORKER) {
    lastOfferFromHcf =
      findLast(negotiationHistory, (historyItem: RateNegotiationHistoryResponse) => {
        return historyItem.createdByUserRole === NegotiationRateRole.WORKPLACE;
      })?.payRate ?? 0;

    lastOfferByHcp = rateNegotiation?.lastOffer?.hourlyPay ?? 0;
  }

  const shiftPayRate = shift?.offer?.attributes.hourlyPay ?? 0;

  return {
    workerLastProposal: lastOfferByHcp,
    lowerLimit: getLowerLimit({ flagLimits, lastOfferFromHcf, shiftPayRate }),
    upperLimit: Math.min(
      getUpperLimit({
        flagLimits,
        agentReq: shift?.attributes.qualification,
        lastOfferByHcp,
        shiftPayRate,
      }),
      negotiationShiftDetails?.data.attributes.maxPayRate ?? Number.MAX_SAFE_INTEGER
    ),
  };
}

export function formatNegotiationDate(dateString: string | undefined): string {
  if (!dateString) {
    return "";
  }

  const date = new Date(dateString);
  const now = new Date();

  if (isBefore(date, startOfDay(subDays(now, 1)))) {
    return format(date, "MM/dd/yyyy");
  }

  if (isSameDay(date, now)) {
    return `Today ${format(date, "hh:mm a")}`;
  }

  return `Yesterday ${format(date, "hh:mm a")}`;
}

export function getNegotiationButtonText(parameters: {
  rateNegotiationStatus: ShiftRateNegotiationStatus | undefined;
  rateNegotiationLastOfferBy: ShiftRateNegotiationRole | undefined;
  rateNegotiationLastOfferPayRate: number | undefined;
}) {
  const { rateNegotiationStatus, rateNegotiationLastOfferBy, rateNegotiationLastOfferPayRate } =
    parameters;

  if (!isDefined(rateNegotiationStatus)) {
    return "Negotiate rate";
  }

  // FIXME: Automatically disabled during ESLint config update
  switch (rateNegotiationStatus) {
    case ShiftRateNegotiationStatus.OPEN: {
      if (
        rateNegotiationLastOfferBy === ShiftRateNegotiationRole.WORKPLACE &&
        isDefined(rateNegotiationLastOfferPayRate)
      ) {
        return `Reply to proposal: ${formatDollarsAsUsd(rateNegotiationLastOfferPayRate, {
          minimumFractionDigits: 0,
        })}/hr`;
      }

      return "View proposal";
    }

    case ShiftRateNegotiationStatus.EXPIRED: {
      return "Proposal expired";
    }

    case ShiftRateNegotiationStatus.REJECTED: {
      if (rateNegotiationLastOfferBy === ShiftRateNegotiationRole.WORKPLACE) {
        return "Proposal rejected";
      }

      return undefined;
    }

    default: {
      return undefined;
    }
  }
}
